/**
 * Component to gate features based on tier feature flags
 * Shows upgrade prompt if feature is not available
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Paper } from '@mui/material';
import { Lock, Upgrade } from '@mui/icons-material';
import { useTierFeatures } from '../hooks/useTierFeatures';
import { useCompany } from '../hooks/useCompany';

interface RequireFeatureProps {
  feature: 'analytics' | 'approvalSystem' | 'landingPageBuilder' | 'allQuestionTypes' | 'companyAccount' | 'calendarView';
  children: React.ReactNode;
  redirect?: boolean; // If true, redirects instead of showing message
}

const RequireFeature: React.FC<RequireFeatureProps> = ({ 
  feature, 
  children, 
  redirect = false 
}) => {
  const navigate = useNavigate();
  const { canUseFeature, loading: tierLoading } = useTierFeatures();
  const { isAdmin, loading: companyLoading } = useCompany();

  const loading = tierLoading || companyLoading;

  // Admin/Owner users should always have access (Managers are subject to tier)
  const hasTierAccess = canUseFeature(feature);
  const hasAccess = hasTierAccess || isAdmin;

  useEffect(() => {
    if (!loading && !hasAccess && redirect) {
      navigate('/payment-upgrade', {
        state: {
          feature,
          message: `This feature is not available in your current plan. Upgrade to access ${feature}.`
        }
      });
    }
  }, [hasAccess, loading, redirect, navigate, feature]);

  // If loading is done and user is admin, allow access immediately
  if (!loading && isAdmin) {
    return <>{children}</>;
  }

  // Show loading while company or tier data is being fetched
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!hasAccess) {
    if (redirect) {
      return null; // Will redirect
    }

    const featureNames: Record<string, string> = {
      analytics: 'Analytics',
      approvalSystem: 'Approval System',
      landingPageBuilder: 'Landing Page Builder',
      allQuestionTypes: 'Advanced Question Types',
      companyAccount: 'Company Accounts',
      calendarView: 'Calendar View',
    };

    return (
      <Paper sx={{ p: 4, mt: 4, textAlign: 'center' }}>
        <Lock sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" gutterBottom fontWeight="bold">
          {featureNames[feature]} Not Available
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          This feature is not available in your current subscription tier.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Upgrade />}
          onClick={() => navigate('/payment-upgrade')}
          sx={{ mt: 2 }}
        >
          Upgrade Your Plan
        </Button>
      </Paper>
    );
  }

  return <>{children}</>;
};

export default RequireFeature;
